import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';

import { ApiError } from '@perplexicon/api-client';

/**
 * The single TanStack Query client for all server state (TECH_STACK §12:
 * "Do not duplicate API state into a second global client-state system").
 *
 * Queries do not retry `UNAUTHENTICATED` (expected for anonymous visitors
 * checking the current user) or client-input errors (`VALIDATION_ERROR`,
 * `USERNAME_TAKEN`, etc. — retrying would just reproduce the same
 * rejection); everything else gets TanStack Query's default retry
 * behavior for transient/network failures.
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError) {
    if (error.status >= 400 && error.status < 500) return false;
  }
  return failureCount < 2;
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: handleAuthenticationFailure }),
  mutationCache: new MutationCache({ onError: handleAuthenticationFailure }),
  defaultOptions: {
    queries: {
      retry: shouldRetry,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
    },
  },
});

/**
 * Any protected endpoint can discover that a browser-restored session has
 * expired. Reconcile the single auth cache immediately so ProtectedRoute
 * redirects to login, and discard the previous account's cached data.
 */
function handleAuthenticationFailure(error: unknown): void {
  if (
    !(error instanceof ApiError) ||
    (error.code !== 'SESSION_EXPIRED' && error.code !== 'UNAUTHENTICATED')
  ) {
    return;
  }
  queueMicrotask(() => {
    queryClient.removeQueries({
      predicate: (query) => query.queryKey[0] !== 'auth',
    });
    queryClient.setQueryData(['auth', 'me'], null);
  });
}
